import { motion } from "framer-motion";
import { Hero } from "../components/Hero";
import { ImpactStats } from "../components/ImpactStats";
import { Testimonials } from "../components/Testimonials";
import { FaqSection } from "../components/FaqSection";
import { getFeatureFlags } from "../utils/adminStore";

const coreServices = [
  {
    title: "IT & ITeS Services",
    text: "Deterministic software engineering, distributed systems architecture, microservices data integration, and network infrastructure.",
    anchor: "it-ites",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25h-13.5A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25h-13.5A2.25 2.25 0 013 12V5.25" />
      </svg>
    ),
  },
  {
    title: "SaaS & Digital Platforms",
    text: "Multi-tenant cloud platforms, RaBuOS business operating platform, dynamic CMS applications, and managed high-concurrency hosting.",
    anchor: "saas-platforms",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125v-3.75" />
      </svg>
    ),
  },
  {
    title: "Research & Development",
    text: "Applied electronic prototyping, embedded C/C++ firmware, LoRaWAN wireless sensor networks, and specialized hardware laboratory engineering.",
    anchor: "rnd-hardware",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5.032 14.47A4.5 4.5 0 008.212 22h7.576a4.5 4.5 0 003.18-7.53l-4.06-4.061a2.25 2.25 0 01-.658-1.591V3.104M9.75 3.104c-.251.037-.502.082-.75.137M9.75 3.104h4.5m0 0c.25.037.5.082.75.137M12 15.75h.008v.008H12v-.008z" />
      </svg>
    ),
  },
  {
    title: "Robotics & Automation",
    text: "Industrial IoT (IIoT) motion control, PLC/SCADA integration, real-time edge telemetry, and preventative maintenance for automated production.",
    anchor: "robotics-automation",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
      </svg>
    ),
  },
  {
    title: "Security & Surveillance Systems",
    text: "Smart IP surveillance networks, biometric access control, perimeter intrusion alarms, network firewall hardening, and CERT-In aligned audits.",
    anchor: "security-systems",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751A11.959 11.959 0 0112 2.714z" />
      </svg>
    ),
  },
  {
    title: "Technology Training & Education",
    text: "Enterprise developer upskilling, corporate technology programs, institutional laboratory setup, and structured engineering tracks in Full-Stack, IoT & Embedded systems.",
    anchor: "training-education",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.62 48.62 0 0112 20.904a48.62 48.62 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-5.25 6.557c0 1.5 1.5 3 5.25 3s5.25-1.5 5.25-3m-10.5 0h10.5" />
      </svg>
    ),
  },
];

const ecosystemHighlights = [
  {
    badge: "Enterprise SaaS Platform (In Development)",
    title: "RaBuOS (Radiece Business OS)",
    desc: "An all-in-one business operating platform in active development (Public Beta Q4 2026). Engineered to automate MCA statutory compliance, GST schedules, visual task DAGs, Gantt roadmaps, and document version governance on Bun.",
    link: "/services#saas-platforms",
    linkText: "Explore Platform Architecture & Roadmap →"
  },
  {
    badge: "Hardware & IoT Lab",
    title: "Deep-Tech Prototyping & Robotics",
    desc: "In-house electronic lab engineering custom ESP32/ARM firmware, LoRaWAN wireless IoT telemetry sensor networks in the 865–867 MHz band, and industrial control automation with end-to-end verification.",
    link: "/services#rnd-hardware",
    linkText: "Discover R&D Capabilities →"
  },
  {
    badge: "Statutory & Security Standards",
    title: "Enterprise Governance & Compliance",
    desc: "Built with strict compliance to the Digital Personal Data Protection (DPDP) Act 2023, CERT-In security directives, ISO/IEEE standards, and transparent Annual Maintenance Contracts (AMC).",
    link: "/security",
    linkText: "Review Security & Governance Standards →"
  }
];

export function HomePage() {
  const flags = getFeatureFlags();

  return (
    <>
      <Hero />

      {/* Live Impact Counters & Case Studies */}
      <ImpactStats />

      {/* Core Practice Verticals */}
      <section className="no-print bg-slate-50 dark:bg-[#070b12] py-16 sm:py-20 border-b border-slate-200/80 dark:border-slate-800/80 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="text-center mb-12"
          >
            <p className="text-brand dark:text-primary font-semibold text-xs uppercase tracking-wider mb-2">Practice Verticals</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
              Integrated Technology Engineering Capabilities
            </h2>
            <p className="mt-3 text-slate-500 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed text-sm sm:text-base font-light">
              From enterprise software platforms to embedded electronic hardware and industrial telemetry, we deliver dependable execution across 6 multidisciplinary verticals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreServices.map((s, idx) => (
              <motion.a
                key={s.title}
                href={`#/services#${s.anchor}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: idx * 0.06, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                className="card-interactive bg-white dark:bg-[#0e1724] rounded-2xl p-6 sm:p-7 shadow-xs border border-slate-200/80 dark:border-slate-800 hover:border-brand/40 dark:hover:border-primary/40 flex flex-col justify-between group transition-colors"
              >
                <div>
                  <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800/80 text-brand dark:text-primary rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary/15 group-hover:text-brand group-hover:scale-105 transition-all duration-200 border border-slate-200/60 dark:border-slate-700">
                    {s.icon}
                  </div>
                  <h3 className="text-lg font-bold text-brand-dark dark:text-slate-100 mb-2 group-hover:text-brand dark:group-hover:text-primary transition-colors">{s.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-light">{s.text}</p>
                </div>
                <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center text-xs font-bold text-brand dark:text-primary group-hover:text-primary dark:group-hover:text-sky-300 transition-colors uppercase tracking-wider">
                  Explore Vertical →
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Ecosystem & Innovation Spotlight */}
      <section className="no-print bg-white dark:bg-[#0b131e] py-16 sm:py-24 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="text-center max-w-3xl mx-auto mb-14"
          >
            <span className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 text-brand dark:text-primary text-xs font-semibold uppercase tracking-wider mb-3">
              Deep-Tech Engineering Ecosystem
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-dark dark:text-slate-100 tracking-tight">
              Bridging Physical Electronics & Cloud Software
            </h2>
            <p className="mt-3 text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed font-light">
              Headquartered in Kakkanad, Ernakulam, we engineer integrated systems combining hardware prototyping, low-power telemetry networks, multi-tenant cloud software, and enterprise governance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ecosystemHighlights.map((eco, idx) => (
              <motion.div
                key={eco.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                className="card-interactive bg-slate-50 dark:bg-[#0e1724] border border-slate-200/90 dark:border-slate-800 rounded-2xl p-7 sm:p-8 flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
              >
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-primary bg-slate-900 px-3 py-1 rounded-full inline-block mb-4">
                    {eco.badge}
                  </span>
                  <h3 className="text-xl font-bold text-brand-dark dark:text-slate-100 mb-3">
                    {eco.title}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed font-light mb-6">
                    {eco.desc}
                  </p>
                </div>

                <div className="pt-4 border-t border-slate-200/80 dark:border-slate-800">
                  <a
                    href={eco.link}
                    className="text-xs font-bold uppercase tracking-wider text-brand dark:text-primary hover:text-primary dark:hover:text-sky-300 transition-colors inline-flex items-center gap-1.5"
                  >
                    {eco.linkText}
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Verified Partner Reviews & Testimonials */}
      {flags.enableTestimonials && <Testimonials />}

      {/* Careers Callout Section */}
      <section className="no-print bg-gradient-to-r from-[#080d16] via-[#0b131e] to-[#080d16] py-14 sm:py-16 text-white border-y border-white/10 relative overflow-hidden">
        <div
          className="absolute -right-20 -top-20 w-80 h-80 bg-primary/10 rounded-full blur-3xl pointer-events-none ambient-glow"
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-2xl text-center lg:text-left"
          >
            <span className="inline-block px-3 py-1 bg-primary/20 border border-primary/30 text-primary text-xs font-bold rounded-full uppercase tracking-wider mb-3">
              Actively Hiring • Remote Opening
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
              UI/UX Designer & Developer Opportunity
            </h2>
            <p className="text-slate-300 text-sm sm:text-base mt-2 leading-relaxed font-light">
              We are hiring a creative UI/UX Designer & Developer (1–2 years exp, Remote / Work from Home) to design software products, build clean front-end interfaces, and support corporate branding.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center justify-center gap-3 shrink-0"
          >
            <motion.a
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              href="/careers"
              className="btn-shine inline-flex items-center gap-2 bg-primary hover:bg-sky-400 text-slate-950 px-6 py-3 rounded-xl font-bold text-xs uppercase tracking-wider shadow-lg"
            >
              View Role & Apply Online →
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              href="/about"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-5 py-3 rounded-xl font-semibold text-xs uppercase tracking-wider border border-white/20 transition-all"
            >
              Our Company Vision
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Technical FAQ Accordion */}
      <FaqSection />

      {/* Contact Call to Action */}
      <section className="no-print bg-[#080d16] py-16 sm:py-20 text-white border-t border-slate-800">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-4">
            Ready to Engineer Your System Solution?
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-base sm:text-lg mb-8 leading-relaxed font-light">
            Whether you require custom enterprise software, hardware R&D, security infrastructure, or corporate technical upskilling, our engineering team is ready to discuss your goals.
          </p>
          <motion.a
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            href="/contact"
            className="inline-flex items-center gap-2 bg-primary hover:bg-sky-400 text-slate-950 px-8 py-3.5 rounded-xl font-bold text-base transition-colors shadow-lg"
          >
            Go to Contact Page & Inquire →
          </motion.a>
        </motion.div>
      </section>
    </>
  );
}
